/**
 * Unlocked sessions.
 *
 * Security rule #1 of this module: **no accessor returns the private key except
 * `exportSecret`**. A `Session` is therefore an inert, frozen descriptor — account id,
 * public key, fingerprint, party id — and the key material lives in a module-private
 * `WeakMap` that nothing outside this file can reach. A consumer cannot write
 * `session.privateKey` (frozen object), cannot read it (never stored on the object), and
 * cannot forge a session (a hand-made object simply is not in the WeakMap).
 *
 * `WeakMap` also means a dropped session becomes garbage-collectable together with its key.
 * `lock()` does not wait for that: it zeroes the bytes immediately.
 */

import { wipe } from './bytes';
import { SessionLockedError } from './errors';
import type { KeyMaterial } from './keys';

/**
 * Handle on an unlocked wallet. Everything on it is public information.
 * Obtain one with `unlockVault`; destroy it with `lock`.
 */
export interface Session {
  readonly accountId: string;
  /** Raw Ed25519 public key, 64 hex chars. */
  readonly publicKeyHex: string;
  /** Canton fingerprint, 68 hex chars. */
  readonly fingerprint: string;
  /** `<hint>::<fingerprint>` — the Canton party this session signs for. */
  readonly partyId: string;
  /** Epoch millis at which the session was unlocked. */
  readonly unlockedAt: number;
  /** False once `lock()` has been called. */
  isUnlocked(): boolean;
}

interface SessionSecret {
  privateKey: Uint8Array;
  mnemonic?: string;
}

/**
 * The only reference to live key material in the whole app.
 * Module-private on purpose: not exported, not reachable from a `Session` value.
 */
const SECRETS = new WeakMap<Session, SessionSecret>();

export interface SessionDescriptor {
  accountId: string;
  publicKeyHex: string;
  fingerprint: string;
  partyId: string;
}

/** Internal: builds a frozen session and stashes its key material out of reach. */
export function createSession(descriptor: SessionDescriptor, material: KeyMaterial): Session {
  const session: Session = Object.freeze({
    accountId: descriptor.accountId,
    publicKeyHex: descriptor.publicKeyHex,
    fingerprint: descriptor.fingerprint,
    partyId: descriptor.partyId,
    unlockedAt: Date.now(),
    isUnlocked(): boolean {
      return SECRETS.has(session);
    },
  });

  SECRETS.set(session, { privateKey: material.privateKey, mnemonic: material.mnemonic });
  return session;
}

/** Internal: the private scalar, or {@link SessionLockedError}. Never leaves this module. */
export function requireSecret(session: Session): SessionSecret {
  const secret = session ? SECRETS.get(session) : undefined;
  if (!secret) {
    throw new SessionLockedError('Wallet session is locked or invalid — unlock the vault again');
  }
  return secret;
}

/**
 * Destroys a session: zeroes the private key bytes and drops the WeakMap entry.
 * Idempotent, and safe to call on an already-locked session.
 *
 * Caveat worth knowing: the mnemonic is a JS string and cannot be wiped — dropping the
 * reference is all any JS runtime can do.
 */
export function lock(session: Session): void {
  const secret = session ? SECRETS.get(session) : undefined;
  if (!secret) return;
  wipe(secret.privateKey);
  secret.mnemonic = undefined;
  SECRETS.delete(session);
}

/** True when the value is a live session created by this module. */
export function isUnlockedSession(session: Session | null | undefined): session is Session {
  return !!session && SECRETS.has(session);
}
